import React, { FC } from 'react';

import cx from 'classnames';
import { isEmpty } from 'ramda';
import { Field, useForm } from 'react-final-form';

import AddMinusButton from 'components/shared/AddMinusButton';
import { useHanlders } from 'components/shared/ArrayFields/functions';
import { IArrayFieldsProps, IBlockProps, ICustomInputProps } from 'components/shared/ArrayFields/types';
import InputError from 'components/shared/FormAdapters/FormDefault/FormGroup/Error';
import Input from 'components/shared/FormAdapters/FormDefault/Input';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';

import Divider from '../Divider';
import FormActions from '../FormAdapters/FormDefault/FormActions';
import Label from '../FormAdapters/FormDefault/Label';
import LabelWrapper from '../FormAdapters/FormDefault/Label/Wrapper';
import TextArea from '../FormAdapters/FormDefault/TextArea';
import Icon from '../Icon';
import styles from './index.module.css';

const defaultInitialValues = {};
/**
 * Custom solution to Array Fields
 *
 * The goal is to keep the form state clean
 * Only valuable data is stored into the form state -- so no more empty objects [{}] and tedious checks outside the form in order to filter this data
 * Killer feature is that when the user manually clears all inputs of particular block (without directly deleting it) the block will be removed
 * from the form state but for the user the block will still be present
 * Also knows how to work with remote data (that has IDs), it will be stored into the form state with the _destroy field
 */
const ArrayFields: FC<IArrayFieldsProps> = ({ data, initialValues = defaultInitialValues }) => {
  const { blocks, createBlock, deleteBlock, updateValue, findBlockIndexRegisteredInForm, localIdKey } = useHanlders({
    initialValues,
    data,
  });

  const { sectionName, fieldsData, config = {} } = data;
  const {
    withDivider = true,
    twoColumn = false,
    withBlockRemoving = true,
    withBlockAdding = true,
    withBackground = false,
  } = config;
  const form = useForm();
  const { isTabletOrMobile } = useMediaBreakpoints();
  const columnCount = twoColumn && !isTabletOrMobile ? 2 : 1;

  return (
    <div>
      {blocks.map((block: IBlockProps & Record<string, any>, index) => {
        const blockIndexInForm = findBlockIndexRegisteredInForm(block);

        return (
          <div key={block.id ?? block[localIdKey]} className={cx(columnCount === 2 && styles.blockTwoColumn)}>
            {withDivider && index !== 0 && <Divider />}

            <div className={cx(styles.fieldsContainer, 'custom-array-fields-container')}>
              {fieldsData.map(({ name: fieldName, label, type = 'text', ...restData }, fieldIndex, fieldArray) => {
                const name = `${sectionName}[${blockIndexInForm}].${fieldName}`;
                const fieldState = form.getFieldState(name) ?? {};
                const isLastField = fieldIndex === fieldArray.length - 1;
                const shouldShowMargin =
                  (!isLastField && columnCount === 2 && fieldIndex % 2 === 1) || (!isLastField && columnCount === 1);
                const showDeleteBtnFieldIndex = fieldArray.length === 1 || columnCount === 1 ? 0 : 1;

                return (
                  <div key={fieldName} className={styles.inputContainer}>
                    <CustomInput
                      {...restData}
                      type={type}
                      name={name}
                      label={label}
                      blockIndexInForm={blockIndexInForm}
                      fieldState={fieldState}
                      value={block[fieldName] ?? ''}
                      onChange={(e) => {
                        updateValue(block, fieldName, e.currentTarget.value);
                      }}
                      showDeleteBtn={withBlockRemoving && showDeleteBtnFieldIndex === fieldIndex}
                      onDelete={() => deleteBlock(block)}
                      withBackground={withBackground}
                    />

                    {shouldShowMargin && <div className={cx(styles.fieldsMargin, 'custom-array-fields-margin')}></div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {withBlockAdding && (
        <FormActions isCenter>
          <AddMinusButton className={styles.createBlockBtn} onClick={() => createBlock()} />
        </FormActions>
      )}
    </div>
  );
};

export default ArrayFields;

const map: Record<string, any> = {
  textarea: TextArea,
  text: Input,
};

const CustomInput: FC<ICustomInputProps> = ({
  showDeleteBtn,
  onDelete,
  name,
  blockIndexInForm,
  fieldState,
  label,
  type,
  ...restProps
}) => {
  const Component = map[type!];

  return (
    <div>
      {label && (
        <LabelWrapper>
          {/*@ts-ignore*/}
          <Label noTransform>{label}</Label>
        </LabelWrapper>
      )}

      <div className={styles.inputWrap}>
        <Component {...restProps} />

        {showDeleteBtn && (
          <Icon
            component={MinusIcon}
            onClick={onDelete}
            className={cx(styles.blockRemoveBtn, 'custom-array-fields-block-remove-btn')}
          />
        )}
      </div>

      {!isEmpty(fieldState) && fieldState.touched && fieldState.error && <InputError error={fieldState.error} />}
      {blockIndexInForm !== -1 && <Field name={name}>{() => null}</Field>}
    </div>
  );
};

function MinusIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.51013 0C4.69881 0 1.3292 2.55813 0.314064 6.22249C0.196543 6.61495 0.119547 7.00538 0.0810489 7.39175L0.0405244 7.73134C0.0202622 7.99163 0 8.26615 0 8.5C0 8.87213 0.0202622 9.22596 0.0587604 9.58385C0.113468 10.0536 0.220858 10.5477 0.378903 11.052L0.38093 11.0541L0.411323 11.1293C0.636234 11.8248 0.925983 12.4511 1.27652 12.992C1.29476 13.0144 1.30691 13.0347 1.31502 13.051L1.40822 13.1892C1.69392 13.6366 2.04243 14.0616 2.50441 14.5252C3.3149 15.3386 4.24899 15.9589 5.27831 16.3696C5.90441 16.6238 6.55483 16.8028 7.2174 16.9004C7.71383 16.9675 8.10691 16.998 8.48987 16.998C8.83027 16.998 9.18284 16.9776 9.50703 16.939C9.95888 16.8861 10.4391 16.7865 11.0206 16.6218C14.5928 15.5054 16.998 12.2315 16.998 8.4756C17 3.80467 13.1907 0 8.51013 0ZM10.97 15.1902C10.4411 15.3976 9.92646 15.4993 9.49285 15.5725C9.14636 15.6172 8.81204 15.6376 8.48987 15.6376C7.06746 15.6376 5.70584 15.2268 4.55089 14.4459C3.91871 14.0169 3.41013 13.5471 2.99273 13.0123L2.92384 12.9411C2.47199 12.3779 2.11538 11.7597 1.86818 11.1029L1.84589 11.0541C1.64934 10.5742 1.52372 10.0821 1.46091 9.55132C1.41836 9.20156 1.37783 8.85586 1.37783 8.5V8.47967C1.37783 8.26208 1.39607 8.04856 1.4143 7.82081L1.42443 7.70694L1.4447 7.60526C1.49535 7.225 1.5764 6.74916 1.76079 6.23876C2.75566 3.34916 5.46877 1.40311 8.51013 1.40311C12.4207 1.40311 15.6019 4.59569 15.6019 8.52034C15.6019 11.4689 13.7398 14.149 10.97 15.1902Z"
        fill="currentColor"
      />
      <path
        d="M9.05703 7.9104H12.0356C12.1977 7.9104 12.3598 7.9714 12.4611 8.09341C12.5827 8.21542 12.6434 8.3781 12.6434 8.54078C12.6434 8.70346 12.5827 8.86614 12.4611 8.98815C12.3395 9.11016 12.1774 9.17117 12.0153 9.17117H9.05703H7.80078H4.96407C4.63987 9.17117 4.33594 8.86614 4.33594 8.54078C4.33594 8.3781 4.39672 8.21542 4.5183 8.09341C4.63987 7.9714 4.80197 7.9104 4.96407 7.9104H7.80078H9.05703Z"
        fill="currentColor"
      />
    </svg>
  );
}
